import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  LinearProgress,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import {
  AccountBalanceOutlined,
  CheckCircle,
  EmailOutlined,
  LockOutlined,
  RadioButtonUnchecked,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@api/api";
import IdentidadeVisual from "../../estilos/identidadeVisual";

const AZUL_CADASTRO = "#0060ef";

const campoCadastroSx = {
  "&&& .MuiInputLabel-root": {
    color: AZUL_CADASTRO,
    fontSize: "14px",
  },

  "&&& .MuiInputLabel-root.Mui-focused": {
    color: AZUL_CADASTRO,
  },

  "&&& .MuiInputLabel-root.Mui-error": {
    color: "#d32f2f",
  },

  "&&& .MuiOutlinedInput-root": {
    height: "34px",
    borderRadius: "6px",
    background: "linear-gradient(135deg, #ffffff 0%, #f3f7ff 100%)",
    color: "#182039",
    fontSize: "14px",
    px: "12px",

    "& fieldset": {
      borderColor: "#a4bfff",
      borderWidth: "1px",
    },

    "&:hover fieldset": {
      borderColor: "#7ca6fa",
    },

    "&.Mui-focused fieldset": {
      borderColor: AZUL_CADASTRO,
      borderWidth: "1.5px",
    },

    "&.Mui-error fieldset": {
      borderColor: "#d32f2f",
    },
  },

  "&&& .MuiOutlinedInput-root .MuiOutlinedInput-input": {
    fontSize: "13px",
    lineHeight: 1.25,
    height: "auto",
    minWidth: 0,
    p: "7px 0",

    "&::placeholder": {
      color: "#91a0b7",
      opacity: 1,
    },
  },

  "&&& .MuiFormHelperText-root": {
    fontSize: "11px",
    lineHeight: 1.3,
    mx: 0,
  },
};

const layoutCadastroSx = {
  minHeight: "100vh",

  "@supports (min-height: 100dvh)": {
    minHeight: "100dvh",
  },

  boxSizing: "border-box",
  px: { xs: "12px", sm: "16px" },
  py: "8px",
};

const conteudoCadastroSx = {
  width: "100%",
  maxWidth: "386px",
  mx: "auto",

  "& *, & *::before, & *::after": {
    boxSizing: "border-box",
  },

  "&& h1.MuiTypography-root": {
    fontSize: "22px",
    lineHeight: 1.2,
  },

  "&& .MuiButton-root": {
    fontSize: "14px",
    lineHeight: 1.2,
  },
};

function requisitosDaSenha(senha) {
  return [
    {
      texto: "Mínimo 8 caracteres",
      valido: senha.length >= 8,
    },
    {
      texto: "1 letra maiúscula",
      valido: /[A-Z]/.test(senha),
    },
    {
      texto: "1 letra minúscula",
      valido: /[a-z]/.test(senha),
    },
    {
      texto: "1 número",
      valido: /\d/.test(senha),
    },
    {
      texto: "1 símbolo (@$!%*?&)",
      valido: /[@$!%*?&]/.test(senha),
    },
  ];
}

export default function Cadastro() {
  const navigate = useNavigate();

  const [schoolName, setSchoolName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [cadastroConcluido, setCadastroConcluido] = useState(false);

  const [aviso, setAviso] = useState({
    aberto: false,
    mensagem: "",
    severidade: "success",
  });

  const requisitos = requisitosDaSenha(password);

  const caracteresPermitidos = /^[A-Za-z\d@$!%*?&]*$/.test(password);

  const quantidadeAtendida = requisitos.filter((item) => item.valido).length;

  const senhaValida =
    quantidadeAtendida === requisitos.length && caracteresPermitidos;

  const nivel = caracteresPermitidos
    ? quantidadeAtendida
    : Math.min(quantidadeAtendida, 4);

  const forca = !password
    ? { texto: "Não informada", cor: "#697689" }
    : senhaValida
      ? { texto: "Forte", cor: "#388e3c" }
      : nivel >= 3
        ? { texto: "Média", cor: "#ad6200" }
        : { texto: "Fraca", cor: "#d32f2f" };

  const senhasDiferentes =
    confirmPassword.length > 0 && password !== confirmPassword;

  const bloqueado = loading || cadastroConcluido;

  useEffect(() => {
    if (!cadastroConcluido) return;

    const timer = window.setTimeout(() => {
      navigate("/login");
    }, 3000);

    return () => window.clearTimeout(timer);
  }, [cadastroConcluido, navigate]);

  const mostrarAviso = (mensagem, severidade) => {
    setAviso({
      aberto: true,
      mensagem,
      severidade,
    });
  };

  const fecharAviso = (_, motivo) => {
    if (motivo === "clickaway") return;

    setAviso((atual) => ({
      ...atual,
      aberto: false,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (bloqueado) return;

    if (!schoolName.trim()) {
      mostrarAviso("Informe o nome da instituição.", "error");
      return;
    }

    if (!senhaValida) {
      mostrarAviso(
        "Atenda aos requisitos da senha e use os caracteres permitidos.",
        "error",
      );
      return;
    }

    if (password !== confirmPassword) {
      mostrarAviso("As senhas não coincidem.", "error");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(api.register(), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          nome: schoolName.trim(),
          email: email.trim(),
          senha: password,
          regra: "user",
        }),
      });

      const data = await response.json().catch(() => ({}));
      const retorno = data.msg || data.message;

      const mensagem = Array.isArray(retorno) ? retorno.join(" ") : retorno;

      if (!response.ok) {
        mostrarAviso(
          mensagem || "Não foi possível realizar o cadastro.",
          "error",
        );
        return;
      }

      setCadastroConcluido(true);

      mostrarAviso(
        "Cadastro realizado com sucesso! Você será redirecionado para o login.",
        "success",
      );
    } catch {
      mostrarAviso(
        "Não foi possível conectar ao servidor. Tente novamente.",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const inicioCampo = (Icone) => (
    <InputAdornment position="start">
      <Icone
        sx={{
          color: AZUL_CADASTRO,
          fontSize: 20,
        }}
      />
    </InputAdornment>
  );

  const adornosSenha = (visivel, alterarVisibilidade, descricao) => ({
    startAdornment: inicioCampo(LockOutlined),

    endAdornment: (
      <InputAdornment position="end">
        <IconButton
          type="button"
          size="small"
          aria-label={`${visivel ? "Ocultar" : "Mostrar"} ${descricao}`}
          aria-pressed={visivel}
          disabled={bloqueado}
          onClick={() => alterarVisibilidade((atual) => !atual)}
          onMouseDown={(event) => event.preventDefault()}
          edge="end"
          sx={{
            color: AZUL_CADASTRO,
          }}
        >
          {visivel ? (
            <VisibilityOff sx={{ fontSize: 20 }} />
          ) : (
            <Visibility sx={{ fontSize: 20 }} />
          )}
        </IconButton>
      </InputAdornment>
    ),
  });

  const propsCampo = {
    fullWidth: true,
    required: true,
    disabled: bloqueado,
    InputLabelProps: {
      shrink: true,
    },
    sx: campoCadastroSx,
  };

  return (
    <IdentidadeVisual variante="acesso" suave sx={layoutCadastroSx}>
      <Box sx={conteudoCadastroSx}>
        <Box
          sx={{
            textAlign: "center",
            color: "#ffffff",
            mb: "12px",
          }}
        >
          <Typography
            component="div"
            sx={{
              fontSize: "44px",
              fontWeight: 800,
              letterSpacing: "1px",
              lineHeight: "40px",
              textShadow: "0 1px 4px rgba(0, 70, 150, 0.08)",
            }}
          >
            SEAR
          </Typography>

          <Box
            sx={{
              width: "246px",
              maxWidth: "100%",
              height: "1px",
              backgroundColor: "rgba(255, 255, 255, 0.35)",
              mx: "auto",
              my: "4px",
            }}
          />

          <Typography
            component="div"
            sx={{
              fontSize: "13px",
              lineHeight: 1.25,
            }}
          >
            Sistema Eletrônico de Atendimento Remoto
          </Typography>
        </Box>

        <Box
          component="main"
          aria-labelledby="titulo-cadastro"
          sx={{
            width: "100%",
            p: "13px 16px 14px",
            borderRadius: "12px",
            border: "1px solid rgba(220, 232, 250, 0.9)",
            background:
              "linear-gradient(145deg, #ffffff 0%, #fcfdff 58%, #f3f7ff 100%)",
            boxShadow: "0 10px 28px rgba(0, 55, 140, 0.13)",
          }}
        >
          <Box
            sx={{
              width: "44px",
              height: "44px",
              mx: "auto",
              mb: "6px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "50%",
              backgroundColor: "#e7efff",
            }}
          >
            <LockOutlined
              sx={{
                color: AZUL_CADASTRO,
                fontSize: 24,
              }}
            />
          </Box>

          <Typography
            id="titulo-cadastro"
            component="h1"
            sx={{
              mb: "4px",
              textAlign: "center",
              color: "#1f2c51",
              fontSize: "22px",
              fontWeight: 700,
              lineHeight: 1.2,
            }}
          >
            Cadastro
          </Typography>

          <Typography
            component="div"
            sx={{
              mb: "14px",
              textAlign: "center",
              color: "#6b809e",
              fontSize: "12px",
              lineHeight: 1.3,
            }}
          >
            Preencha os dados abaixo para criar sua conta.
          </Typography>

          <Box
            component="form"
            onSubmit={handleSubmit}
            aria-busy={loading}
            sx={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            <TextField
              {...propsCampo}
              id="cadastro-instituicao"
              name="nome"
              label="Nome da Instituição"
              placeholder="Digite o nome da instituição"
              autoComplete="organization"
              value={schoolName}
              onChange={(event) => {
                setSchoolName(event.target.value);
              }}
              InputProps={{
                startAdornment: inicioCampo(AccountBalanceOutlined),
              }}
              sx={{
                ...campoCadastroSx,
                mb: "15px",
              }}
            />

            <TextField
              {...propsCampo}
              id="cadastro-email"
              name="email"
              type="email"
              label="E-mail"
              placeholder="Digite seu e-mail"
              autoComplete="email"
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
              }}
              InputProps={{
                startAdornment: inicioCampo(EmailOutlined),
              }}
              sx={{
                ...campoCadastroSx,
                mb: "15px",
              }}
            />

            <Box sx={{ mb: "14px" }}>
              <TextField
                {...propsCampo}
                id="cadastro-senha"
                name="senha"
                label="Senha"
                placeholder="Digite sua senha"
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                value={password}
                onChange={(event) => {
                  setPassword(event.target.value);
                }}
                error={!caracteresPermitidos}
                helperText={
                  !caracteresPermitidos
                    ? "Use apenas letras sem acento, números e os símbolos @$!%*?&."
                    : ""
                }
                inputProps={{
                  "aria-describedby": caracteresPermitidos
                    ? "requisitos-cadastro"
                    : "requisitos-cadastro cadastro-senha-helper-text",
                }}
                InputProps={adornosSenha(
                  showPassword,
                  setShowPassword,
                  "senha",
                )}
              />

              <Box
                id="requisitos-cadastro"
                sx={{
                  mt: "8px",
                  p: "7px 8px",
                  borderRadius: "8px",
                  border: "1px solid #dfe8f5",
                  backgroundColor: "#f2f6fd",
                }}
              >
                <Typography
                  component="div"
                  sx={{
                    mb: "5px",
                    color: "#46516a",
                    fontSize: "12px",
                    lineHeight: "15px",
                    fontWeight: 600,
                  }}
                >
                  Requisitos da senha:
                </Typography>

                <Box
                  component="ul"
                  sx={{
                    m: 0,
                    p: 0,
                    listStyle: "none",
                    display: "grid",
                    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                    gap: "4px 8px",

                    "& > :last-child": {
                      gridColumn: "1 / -1",
                    },
                  }}
                >
                  {requisitos.map((item) => (
                    <Box
                      component="li"
                      key={item.texto}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        minHeight: "14px",
                        color: item.valido ? "#388e3c" : "#52627b",
                      }}
                    >
                      {item.valido ? (
                        <CheckCircle
                          sx={{
                            fontSize: 12,
                            flexShrink: 0,
                          }}
                        />
                      ) : (
                        <RadioButtonUnchecked
                          sx={{
                            fontSize: 12,
                            flexShrink: 0,
                          }}
                        />
                      )}

                      <Box
                        component="span"
                        sx={{
                          fontSize: "11px",
                          lineHeight: "14px",
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            position: "absolute",
                            width: "1px",
                            height: "1px",
                            p: 0,
                            m: "-1px",
                            overflow: "hidden",
                            clip: "rect(0, 0, 0, 0)",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {item.valido ? "Atendido: " : "Pendente: "}
                        </Box>

                        {item.texto}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>

              <Box sx={{ mt: "6px" }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 1,
                    mb: "4px",
                  }}
                >
                  <Typography
                    id="rotulo-forca-senha"
                    component="div"
                    sx={{
                      color: "#46516a",
                      fontSize: "12px",
                      lineHeight: "14.4px",
                      fontWeight: 600,
                    }}
                  >
                    Força da senha:
                  </Typography>

                  <Typography
                    component="div"
                    sx={{
                      color: forca.cor,
                      fontSize: "11px",
                      lineHeight: "14.4px",
                      fontWeight: 600,
                    }}
                  >
                    {forca.texto}
                  </Typography>
                </Box>

                <LinearProgress
                  variant="determinate"
                  value={(nivel / requisitos.length) * 100}
                  aria-labelledby="rotulo-forca-senha"
                  aria-valuetext={forca.texto}
                  sx={{
                    height: 9,
                    borderRadius: "6px",
                    backgroundColor: "#dfe7f2",

                    "& .MuiLinearProgress-bar": {
                      borderRadius: "6px",
                      backgroundColor: forca.cor,
                    },
                  }}
                />
              </Box>
            </Box>

            <TextField
              {...propsCampo}
              id="cadastro-confirmar-senha"
              name="confirmarSenha"
              label="Confirmar senha"
              placeholder="Digite novamente sua senha"
              type={showConfirmPassword ? "text" : "password"}
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(event) => {
                setConfirmPassword(event.target.value);
              }}
              error={senhasDiferentes}
              helperText={senhasDiferentes ? "As senhas não coincidem." : ""}
              InputProps={adornosSenha(
                showConfirmPassword,
                setShowConfirmPassword,
                "confirmação da senha",
              )}
              sx={{
                ...campoCadastroSx,
                mb: "8px",
              }}
            />

            <Button
              type="submit"
              variant="contained"
              disableElevation
              fullWidth
              disabled={bloqueado}
              sx={{
                height: "34px",
                minHeight: "34px",
                mb: "5px",
                py: 0,
                borderRadius: "7px",
                background: "linear-gradient(180deg, #0870ff 0%, #005cf2 100%)",
                boxShadow: "0 2px 5px rgba(0, 96, 239, 0.14)",
                color: "#ffffff",
                textTransform: "none",
                fontSize: "14px",
                fontWeight: 600,

                "&:hover": {
                  background:
                    "linear-gradient(180deg, #0065f5 0%, #0055df 100%)",
                  boxShadow: "0 2px 5px rgba(0, 96, 239, 0.18)",
                },
              }}
            >
              {loading ? (
                <CircularProgress
                  size={22}
                  color="inherit"
                  aria-label="Cadastrando"
                />
              ) : cadastroConcluido ? (
                "Cadastro realizado"
              ) : (
                "Cadastrar"
              )}
            </Button>

            <Button
              component={Link}
              to="/login"
              variant="outlined"
              fullWidth
              sx={{
                minHeight: 30,
                height: 30,
                py: 0,
                borderRadius: "6px",
                borderColor: "#a4bfff",
                backgroundColor: "rgba(245, 249, 255, 0.65)",
                color: AZUL_CADASTRO,
                textTransform: "none",
                fontSize: "14px",
                fontWeight: 600,

                "&:hover": {
                  borderColor: AZUL_CADASTRO,
                  backgroundColor: "#edf5ff",
                },
              }}
            >
              Voltar para o login
            </Button>
          </Box>
        </Box>
      </Box>

      <Snackbar
        open={aviso.aberto}
        autoHideDuration={5000}
        onClose={fecharAviso}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Alert
          onClose={fecharAviso}
          severity={aviso.severidade}
          sx={{ width: "100%" }}
        >
          {aviso.mensagem}
        </Alert>
      </Snackbar>
    </IdentidadeVisual>
  );
}
